/*
 * i18n (Initialization)
 */
import i18n from "i18next"
import LanguageDetector from "i18next-browser-languagedetector"
import { initReactI18next } from "react-i18next"
import { defaultLocale, LocaleEnum, supportedLocales } from "./intl.constants"

type TranslationResources = {
	[key in LocaleEnum]?: Record<string, unknown>
}

/*
 * Normalize browser language codes (e.g. "en", "en-GB") to the exact locale
 * key used in resources ("en-US" / "fr-CA").  We do this manually here because
 * i18next-browser-languagedetector v8 removed the `convertDetectedLanguage`
 * option; instead we read localStorage ourselves and fall back to the navigator.
 */
function detectLocale(): string {
	const stored = localStorage.getItem("i18nextLng")
	if (stored) {
		if (stored === "fr-CA" || stored.startsWith("fr")) return "fr-CA"
		return "en-US"
	}
	const nav = navigator.language || (navigator as any).userLanguage || ""
	if (nav.startsWith("fr")) return "fr-CA"
	return "en-US"
}

export function initI18n(resources: TranslationResources) {
	const formattedResources = Object.fromEntries(
		Object.entries(resources).map(([locale, translations]) => [
			locale,
			{ translation: translations },
		]),
	)

	// Detect the language before calling init so we can pass it as `lng`,
	// bypassing any async detection that could cause first-render key returns.
	const detectedLng = detectLocale()

	i18n
		.use(LanguageDetector)
		.use(initReactI18next)
		.init({
			resources: formattedResources,
			lng: detectedLng,
			fallbackLng: defaultLocale,
			supportedLngs: supportedLocales,
			nonExplicitSupportedLngs: true,
			initImmediate: false,
			interpolation: {
				escapeValue: false,
			},
			detection: {
				order: ["localStorage", "navigator"],
				lookupLocalStorage: "i18nextLng",
				caches: ["localStorage"],
			},
		})

	return i18n
}

// Singleton re-export so callers can pass the instance to <I18nextProvider>
export { i18n as i18nSingleton }
